import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { LocalTradeFilters } from '@/types';

// MTM filter store — an independent clone of the Trades filter store. The MTM page
// reuses the same LocalTradeFilters shape and the same applyLocalTradeFilters
// predicate, but its filter state must NOT be shared with the Trades tab (per
// LR-394 acceptance: column + filter state independent per tab). The Counterparty
// filter is intentionally unused on MTM — the active subtab fixes the counterparty —
// but the field is kept on the shape so the shared predicate stays uniform.
//
// Intentionally NOT persisted (no `persist` middleware), unlike mtm-column-store:
// filters are per-session so the trader always starts from an unfiltered view of the
// current book rather than re-opening into yesterday's slice. Column layout (order /
// visibility / pins) is structural preference and DOES survive reloads; a filter
// selection is transient query state and should not. Mirrors the Trades stores.
interface MtmFilterStore {
  localMtmFilters: LocalTradeFilters;
  setLocalMtmFilters: (filters: LocalTradeFilters) => void;
  clearLocalMtmFilters: () => void;
  resetLocalFilter: (filterId: string) => void;
}

const defaultLocalFilters: LocalTradeFilters = {
  statuses: [],
  counterparties: [],
  symbols: [],
  tradeTypes: [],
  collateralTypes: [],
  agreementTypes: [],
  termTypes: [],
  legalEntities: [],
  accountIds: [],
  currencies: [],
  settlementCurrencies: [],
  tradeDateFrom: null,
  tradeDateTo: null,
  settlementDateFrom: null,
  settlementDateTo: null,
  maturityDateFrom: null,
  maturityDateTo: null,
  quantityMin: null,
  quantityMax: null,
  contractValueMin: null,
  contractValueMax: null,
  marginMin: null,
  marginMax: null,
  grossPriceMin: null,
  grossPriceMax: null,
  dailyEarningsMin: null,
  dailyEarningsMax: null,
  createdAtFrom: null,
  createdAtTo: null,
  updatedAtFrom: null,
  updatedAtTo: null,
  rateMin: null,
  rateMax: null,
};

export const useMtmFilterStore = create<MtmFilterStore>()(
  devtools(
    (set) => ({
      localMtmFilters: defaultLocalFilters,
      setLocalMtmFilters: (filters) => set({ localMtmFilters: filters }),
      clearLocalMtmFilters: () => set({ localMtmFilters: defaultLocalFilters }),
      resetLocalFilter: (filterId: string) => set((state) => {
        const f = { ...state.localMtmFilters };
        switch (filterId) {
          case 'statuses': f.statuses = []; break;
          case 'counterparties': f.counterparties = []; break;
          case 'symbols': f.symbols = []; break;
          case 'tradeTypes': f.tradeTypes = []; break;
          case 'collateralTypes': f.collateralTypes = []; break;
          case 'agreementTypes': f.agreementTypes = []; break;
          case 'termTypes': f.termTypes = []; break;
          case 'legalEntities': f.legalEntities = []; break;
          case 'accountIds': f.accountIds = []; break;
          case 'currencies': f.currencies = []; break;
          case 'settlementCurrencies': f.settlementCurrencies = []; break;
          case 'tradeDate': f.tradeDateFrom = null; f.tradeDateTo = null; break;
          case 'settlementDate': f.settlementDateFrom = null; f.settlementDateTo = null; break;
          case 'maturityDate': f.maturityDateFrom = null; f.maturityDateTo = null; break;
          case 'quantity': f.quantityMin = null; f.quantityMax = null; break;
          case 'contractValue': f.contractValueMin = null; f.contractValueMax = null; break;
          case 'margin': f.marginMin = null; f.marginMax = null; break;
          case 'grossPrice': f.grossPriceMin = null; f.grossPriceMax = null; break;
          case 'dailyEarnings': f.dailyEarningsMin = null; f.dailyEarningsMax = null; break;
          case 'createdAt': f.createdAtFrom = null; f.createdAtTo = null; break;
          case 'updatedAt': f.updatedAtFrom = null; f.updatedAtTo = null; break;
        }
        return { localMtmFilters: f };
      }),
    }),
    {
      name: 'mtm-filter-store', enabled: import.meta.env.DEV,
    }
  )
);
